import _ from 'lodash';

import { IAutoComplete } from '../../shared/controls/models/auto-complete.model';
import { ILevelDomainData } from 'src/app/infrastructure/models/level-domain-data';
import { IContactForm } from './contact-form';
import { contactTypeData, ContactTypeData, IContactTypeData } from '../../contact-type/models/contact-type/contact-type-data';
import { contactDto, IContactDto } from 'src/assets/common/models/contact-dto.g';
import { IImageBoxData, ImageBoxData } from '../../image/models/image-box-data';
import { IImageParentData, imageParentData } from 'src/app/features/image/models/image-parent-data';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { isEmptyObject } from 'src/app/infrastructure/functions/is-empty-object.function';

export const contactData = {
    ...imageParentData,
    guid: 'guid',
    nr: 'nr',
    name: 'name',
    streetNr: 'streetNr',
    postalCode: 'postalCode',
    town: 'town',
    county: 'county',
    countryCode: 'countryCode',
    country: 'country',
    telephone: 'telephone',
    mobilePhone: 'mobilePhone',
    email: 'email',
    description: 'description',
    deleted: 'deleted',
    inactive: 'inactive',

    photoBoxData: 'photoDto',

    contactTypes: 'contactTypes',
} as const;

export interface IContactCache {
    data: IContactData;
    url: string;
}

export type TContactData = (typeof contactData)[keyof typeof contactData];

export interface IContactData extends ILevelDomainData, IImageParentData
{
    [contactData.guid]: string;
    [contactData.nr]: number | null;
    [contactData.name]: string;
    [contactData.streetNr]: string | null;
    [contactData.postalCode]: string | null;
    [contactData.town]: string | null;
    [contactData.county]: string | null;
    [contactData.countryCode]: string | null;
    [contactData.country]: IAutoComplete | null;
    [contactData.telephone]: string | null;
    [contactData.mobilePhone]: string | null;
    [contactData.email]: string | null;
    [contactData.description]: string | null;
    [contactData.deleted]: boolean;
    [contactData.inactive]: boolean;

    [contactData.photoThumbGuid]: string | null;
    [contactData.photoOriginGuid]: string | null;
    [contactData.photoVersion]: string | null;

    [contactData.photoBoxData]: IImageBoxData | null;
    [contactData.contactTypes]?: IContactTypeData[] | null;
}

export class ContactData 
    implements IContactData, IContactForm, ILevelDomainData
{
    [contactData.guid]!: string;
    [contactData.nr]: number | null = null;
    [contactData.name]!: string;
    [contactData.streetNr]: string | null = null;
    [contactData.postalCode]: string | null = null;
    [contactData.town]: string | null = null;
    [contactData.county]: string | null = null;
    [contactData.countryCode]: string | null = null;
    [contactData.country]: IAutoComplete | null = null;
    [contactData.telephone]: string | null = null;
    [contactData.mobilePhone]: string | null = null;
    [contactData.email]: string | null = null;
    [contactData.description]: string | null = null;
    [contactData.deleted]!: boolean;
    [contactData.inactive]!: boolean;

    [contactData.photoThumbGuid]: string | null = null;
    [contactData.photoOriginGuid]: string | null = null;
    [contactData.photoVersion]: string | null = null;

    [contactData.photoBoxData]: IImageBoxData | null = null;
    [contactData.contactTypes]?: IContactTypeData[] | null = null;

    protected constructor(modelDto?: IContactDto | null);
    protected constructor(modelData: IContactData, modelForm: IContactForm);
    protected constructor(value?: IContactDto | IContactData | null, modelForm?: IContactForm) {
        if (!value || isEmptyObject(value)) return;

        if (!!modelForm) {
            const _modelData = value as IContactData;
            _.assign(this, _modelData, modelForm);
        } else if (!!value) {
            const _modelDto = value as IContactDto;

            this[contactData.guid] = _modelDto[contactDto.guid]!;
            this[contactData.nr] = Number(_modelDto[contactDto.nr]);
            this[contactData.name] = _modelDto[contactDto.name]!;
            this[contactData.streetNr] = _modelDto[contactDto.streetNr];
            this[contactData.postalCode] = _modelDto[contactDto.postalCode];
            this[contactData.town] = _modelDto[contactDto.town];
            this[contactData.county] = _modelDto[contactDto.county];
            this[contactData.countryCode] = _modelDto[contactDto.countryCode];
            this[contactData.telephone] = _modelDto[contactDto.telephone];
            this[contactData.mobilePhone] = _modelDto[contactDto.mobilePhone];
            this[contactData.email] = _modelDto[contactDto.email];
            this[contactData.description] = _modelDto[contactDto.description];
            this[contactData.deleted] = Boolean(_modelDto[contactDto.deleted]);
            this[contactData.inactive] = Boolean(_modelDto[contactDto.inactive]);

            this[contactData.photoThumbGuid] = _modelDto[contactDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
            this[contactData.photoOriginGuid] = _modelDto[contactDto.photoBoxDto]?.[imageBoxDto.photoDto]?.[imageDto.guid] ?? null;
            this[contactData.photoBoxData] = ImageBoxData.create(_modelDto[contactDto.photoBoxDto] ?? null);
            
            this[contactData.contactTypes] = _modelDto?.contactTypesDto
                ?.map(item => ContactTypeData.create(item))
                .filter((item): item is ContactTypeData => item !== null && item[contactTypeData.deleted] === false)
                .sort((a, b) => (a[contactTypeData.description]?.localeCompare(b?.[contactTypeData.description] ?? '')) ?? 0)
                ?? [];
        }
    }
        
    static create(modelDto?: IContactDto | null): IContactData | null
    static create(modelData: IContactData, modelForm: IContactForm): IContactData | null
    static create(value?: IContactDto | IContactData | null, modelForm?: IContactForm): IContactData | null {

        if (!!modelForm) {
            const _modelData = value as IContactData;
            return new ContactData(_modelData, modelForm);
        } else {
            const _modelDto = value as IContactDto;
            return _modelDto ? new ContactData(_modelDto) : null;
        }
    }
}
